/**
 * Scout's meeting-log card (api#1078), read from the API's DATA (api#1089).
 *
 * log_sales_meeting returns its draft — or the one question it needs answered
 * — as `meeting` beside the read-back text: on the stream's `tool_result`
 * event for a live turn, and as `tool_result.meeting` on the stored tool_use
 * row for a conversation reopened from history. The card renders that object.
 * It never reads the text, which the model speaks aloud in voice mode and is
 * free to change. (Until api#1089 this module split that text by its line
 * labels; that is gone.)
 *
 * A result with no `meeting` — a refusal, the confirming call that saved, or
 * a conversation from before api#1089 — has no card. The assistant's own
 * message still says what happened.
 */
import type { ScoutMeetingData, ScoutMeetingState } from '../types/scout';

export const LOG_SALES_MEETING = 'log_sales_meeting';

const STATES: readonly ScoutMeetingState[] = ['draft', 'pick_customer', 'pick_contact', 'ask_paid_by'];

/** The API's `meeting` object, or null when it is absent or not a shape this build knows. */
export function meetingCardFrom(meeting: unknown): ScoutMeetingData | null {
  if (!meeting || typeof meeting !== 'object') return null;
  const m = meeting as ScoutMeetingData;
  return (STATES as readonly string[]).includes(m.state) ? m : null;
}

/** The `meeting` on a stored tool row's result: `{ text, meeting? }`, as an object or a JSON string. */
export function toolResultMeeting(toolResult: unknown): ScoutMeetingData | null {
  if (typeof toolResult === 'string') {
    try { return toolResultMeeting(JSON.parse(toolResult)); } catch { return null; }
  }
  if (toolResult && typeof toolResult === 'object') return meetingCardFrom((toolResult as { meeting?: unknown }).meeting);
  return null;
}

interface StoredRow {
  id: string;
  role: string;
  tool_name?: string | null;
  tool_result?: unknown;
}

/**
 * The card each assistant message carries, keyed by that message's id, from a
 * conversation as GET /ai-agent/conversations/:id/messages returns it (oldest
 * first). A turn is everything after a user row; its LAST log_sales_meeting
 * call is the one the reply is about, so a last call with no card (a refusal,
 * the save) leaves the turn without one rather than with an earlier,
 * superseded card. A turn that died before its reply lends nothing onward.
 */
export function meetingCardsByMessage(rows: StoredRow[]): Map<string, ScoutMeetingData> {
  const out = new Map<string, ScoutMeetingData>();
  let turn: ScoutMeetingData | null = null;
  for (const r of rows) {
    if (r.role === 'user') turn = null;
    else if (r.role === 'tool_use' && r.tool_name === LOG_SALES_MEETING) turn = toolResultMeeting(r.tool_result);
    else if (r.role === 'assistant' && turn) { out.set(r.id, turn); turn = null; }
  }
  return out;
}

/** Exhaustive over the API's activity kinds. An unknown kind reads —, never its key. */
const KIND_LABEL: Record<string, string> = {
  meeting: 'Meeting',
  call: 'Call',
  site_visit: 'Site visit',
  other: 'Other',
};

export function meetingKindLabel(kind: string | null | undefined): string {
  return kind && Object.prototype.hasOwnProperty.call(KIND_LABEL, kind) ? KIND_LABEL[kind] : '—';
}

/** The reply a numbered option sends: its number AND its words, as the rep would say it. */
export function pickReply(c: { n: number; name: string; detail: string | null }): string {
  return `${c.n}. ${c.name}${c.detail ? ` — ${c.detail}` : ''}`;
}

/** What each answer to a card sends, as the rep's own message. */
export const CARD_REPLIES = {
  yes: 'Yes',
  no: 'No',
  company: 'It was on the company card.',
  personal: 'I paid it myself.',
} as const;
